import { AnimationFactory } from "../types";

export const createBlessingBuffAnimation: AnimationFactory = ({
  ctx,
  canvas,
  startX,
  startY,
  endX,
  endY,
  startTime,
  scale,
}) => {
  const duration = 650;          // політ
  const impactDuration = 650;    // ефект на цілі
  const particlesCount = 28;

  const SCALE = scale;

  type Particle = {
    x: number;
    y: number;
    // "домашня" позиція — ціль, до якої частинка підтягується
    tx: number;
    ty: number;
    // орбітальний кут + радіус (для красивого закручування)
    ang: number;
    r: number;
    vr: number;
    vang: number;
    size: number;
    hue: number;
  };

  let particles: Particle[] = [];

  const angle = Math.atan2(endY - startY, endX - startX);

  const rand = (a: number, b: number) => a + Math.random() * (b - a);

  const spawnBlessing = (x: number, y: number) => {
    particles = [];
    for (let i = 0; i < particlesCount; i++) {
      const baseR = rand(18, 60) * SCALE;
      particles.push({
        x: x + Math.cos(Math.random() * Math.PI * 2) * baseR,
        y: y + Math.sin(Math.random() * Math.PI * 2) * baseR,
        tx: x,
        ty: y,
        ang: Math.random() * Math.PI * 2,
        r: baseR,
        vr: rand(0.6, 1.6) * SCALE,          // як швидко стискається до цілі
        vang: rand(0.08, 0.18),              // закручування
        size: rand(2.2, 4.8) * SCALE,
        hue: rand(135, 190),                 // зелено-бірюзова гамма
      });
    }
  };

  const drawSoftOrb = (x: number, y: number, r: number, a: number) => {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(255,255,255,${0.95 * a})`);
    g.addColorStop(0.35, `rgba(180,255,220,${0.75 * a})`);
    g.addColorStop(1, `rgba(60,210,255,0)`);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  };

  const drawRing = (x: number, y: number, r: number, a: number) => {
    const g = ctx.createRadialGradient(x, y, r * 0.6, x, y, r);
    g.addColorStop(0, `rgba(140,255,210,0)`);
    g.addColorStop(0.6, `rgba(140,255,210,${0.35 * a})`);
    g.addColorStop(1, `rgba(80,210,255,0)`);
    ctx.strokeStyle = g;
    ctx.lineWidth = 3 * SCALE;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();
  };

  const drawPlus = (x: number, y: number, size: number, a: number) => {
    ctx.strokeStyle = `rgba(255,255,255,${0.9 * a})`;
    ctx.lineWidth = 2 * SCALE;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x - size, y);
    ctx.lineTo(x + size, y);
    ctx.moveTo(x, y - size);
    ctx.lineTo(x, y + size);
    ctx.stroke();
  };

  return (now: number) => {
    const elapsed = now - startTime;
    const flightT = Math.min(elapsed / duration, 1);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // ====== 1) Політ "благословення" ======
    if (flightT < 1) {
      const x = startX + (endX - startX) * flightT;
      const y = startY + (endY - startY) * flightT;

      // м’який хвіст (не “вогонь”, а “аура”)
      const tailLength = (55 + flightT * 65) * SCALE;
      const tailEndX = x - Math.cos(angle) * tailLength;
      const tailEndY = y - Math.sin(angle) * tailLength;

      const tail = ctx.createLinearGradient(x, y, tailEndX, tailEndY);
      tail.addColorStop(0, "rgba(255,255,255,0.95)");
      tail.addColorStop(0.35, "rgba(150,255,210,0.55)");
      tail.addColorStop(1, "rgba(80,210,255,0)");

      ctx.strokeStyle = tail;
      ctx.lineWidth = 5 * SCALE;
      ctx.beginPath();
      ctx.moveTo(tailEndX, tailEndY);
      ctx.lineTo(x, y);
      ctx.stroke();

      // ядро-орб
      const orbR = (10 + Math.sin(flightT * Math.PI) * 6) * SCALE;
      drawSoftOrb(x, y, orbR + 12 * SCALE, 1);

      // маленькі “іскри” навколо ядра
      const sparkCount = 6;
      for (let i = 0; i < sparkCount; i++) {
        const a = (now / 160 + i) * 1.2;
        const rr = (orbR + 10 * SCALE) * (0.7 + 0.35 * Math.sin(a));
        const sx = x + Math.cos(a) * rr;
        const sy = y + Math.sin(a) * rr;
        ctx.fillStyle = "rgba(255,255,255,0.75)";
        ctx.beginPath();
        ctx.arc(sx, sy, 1.6 * SCALE, 0, Math.PI * 2);
        ctx.fill();
      }

      return true;
    }

    // ====== 2) Імпакт: кільця + частинки “всмоктуються” в ціль ======
    const impactElapsed = elapsed - duration;
    const t = Math.min(impactElapsed / impactDuration, 1);

    if (particles.length === 0) {
      spawnBlessing(endX, endY);
    }

    // 2.1 Пульс-кільця (heal/buff “читабельно”)
    const ringA = 1 - t;
    drawRing(endX, endY, (18 + 85 * t) * SCALE, ringA);
    drawRing(endX, endY, (10 + 55 * t) * SCALE, ringA * 0.9);

    // 2.2 М’яка аура в центрі
    drawSoftOrb(endX, endY, (22 + 18 * (1 - t)) * SCALE, 0.9);

    // 2.3 Частинки “закручуються” і стягуються в ціль
    for (const p of particles) {
      // стискаємо радіус до 0, і трохи крутимо
      p.ang += p.vang * (1 - t);
      p.r = Math.max(0, p.r - p.vr * (1 + 1.5 * t));

      const px = p.tx + Math.cos(p.ang) * p.r;
      const py = p.ty + Math.sin(p.ang) * p.r;

      const alpha = (1 - t) * Math.min(1, (p.r / (20 * SCALE)) + 0.15);

      // hsl дає дуже “живий” бафф (зелено-бірюза)
      ctx.fillStyle = `hsla(${p.hue}, 90%, 70%, ${alpha})`;
      ctx.beginPath();
      ctx.arc(px, py, p.size * (0.6 + 0.8 * (1 - t)), 0, Math.PI * 2);
      ctx.fill();

      // блиск-біленька “іскорка” поверх
      ctx.fillStyle = `rgba(255,255,255,${0.55 * alpha})`;
      ctx.beginPath();
      ctx.arc(px + 1.2 * SCALE, py - 1.2 * SCALE, 1.4 * SCALE, 0, Math.PI * 2);
      ctx.fill();
    }

    // 2.4 Плюсик в кінці (опціонально, але дуже ясно що це heal)
    if (t > 0.15 && t < 0.85) {
      const pulse = 1 + 0.18 * Math.sin((now / 140) * Math.PI);
      drawPlus(endX, endY, 9 * SCALE * pulse, 1 - t);
    }

    return t < 1;
  };
};